import type { WhatsappEditTemplate, WhatsappOriginalTemplate, WhatsappTemplate } from 'types/templates';

export const TEMPLATE_DUPLICATE_NOTICE_DISMISSED_KEY = 'berry-template-duplicate-notice-dismissed';
export const TEMPLATE_EDIT_NOTICE_DISMISSED_KEY = 'berry-template-edit-notice-dismissed';

export function readTemplateDuplicateNoticeDismissed(): boolean {
  try {
    return localStorage.getItem(TEMPLATE_DUPLICATE_NOTICE_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeTemplateDuplicateNoticeDismissed(): void {
  try {
    localStorage.setItem(TEMPLATE_DUPLICATE_NOTICE_DISMISSED_KEY, '1');
  } catch {
    /* ignore */
  }
}

export function readTemplateEditNoticeDismissed(): boolean {
  try {
    return localStorage.getItem(TEMPLATE_EDIT_NOTICE_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeTemplateEditNoticeDismissed(): void {
  try {
    localStorage.setItem(TEMPLATE_EDIT_NOTICE_DISMISSED_KEY, '1');
  } catch {
    /* ignore */
  }
}

const ENGLISH_COPY_SUFFIX = '_copy';
const HEBREW_COPY_SUFFIX = ' - העתק';

function appendEnglishCopySuffix(name: string): string {
  const base = name.trim();
  if (!base) return 'template_copy';
  if (base.endsWith(ENGLISH_COPY_SUFFIX)) return base;
  return `${base}${ENGLISH_COPY_SUFFIX}`;
}

function appendHebrewCopySuffix(niceName: string): string {
  const base = niceName.trim();
  if (!base) return 'העתק';
  if (base.endsWith(HEBREW_COPY_SUFFIX)) return base;
  return `${base}${HEBREW_COPY_SUFFIX}`;
}

function stripEnglishCopySuffix(name: string): string {
  const base = name.trim();
  if (base.endsWith(ENGLISH_COPY_SUFFIX)) {
    return base.slice(0, -ENGLISH_COPY_SUFFIX.length);
  }
  return base;
}

function stripHebrewCopySuffix(niceName: string): string {
  const base = niceName.trim();
  if (base.endsWith(HEBREW_COPY_SUFFIX)) {
    return base.slice(0, -HEBREW_COPY_SUFFIX.length);
  }
  return base;
}

/** Baseline before auto copy suffixes — so prefilled names count as the required change. */
export function buildDuplicateSessionBaseline(template: WhatsappEditTemplate): string {
  return serializeTemplateForDuplicateCompare({
    ...template,
    name: stripEnglishCopySuffix(template.name || ''),
    niceName: stripHebrewCopySuffix(template.niceName || ''),
  });
}

/** Prefill for Meta template create screen — copied from an existing list row. */
export function buildDuplicateTemplatePrefill(
  originalTemplate: WhatsappOriginalTemplate,
  listTemplate: WhatsappTemplate,
): WhatsappEditTemplate {
  const sourceNiceName =
    (listTemplate.hebrewName as { hebrewTemaplateName?: string } | undefined)?.hebrewTemaplateName ||
    listTemplate.hebrewName?.hebrewTemaplateName ||
    '';

  const cloned = JSON.parse(JSON.stringify(originalTemplate)) as WhatsappOriginalTemplate;
  const sourceEnglishName = (cloned.name || listTemplate.name || '').trim();

  return {
    ...cloned,
    id: null,
    name: appendEnglishCopySuffix(sourceEnglishName),
    niceName: appendHebrewCopySuffix(sourceNiceName),
    category: cloned.category || listTemplate.category || '',
    language: cloned.language || listTemplate.langugage || 'he',
    status: undefined,
    last_used: undefined,
  };
}

export function getTemplateDisplayLabel(template: WhatsappTemplate): string {
  const hebrew =
    (template.hebrewName as { hebrewTemaplateName?: string } | undefined)?.hebrewTemaplateName ||
    template.hebrewName?.hebrewTemaplateName ||
    '';
  return hebrew.trim() || template.name || '';
}

/** Stable snapshot for «must change before submit» comparison on duplicate sessions. */
export function serializeTemplateForDuplicateCompare(template: WhatsappEditTemplate): string {
  const snapshot = {
    name: template.name?.trim() ?? '',
    niceName: template.niceName?.trim() ?? '',
    category: template.category ?? '',
    language: template.language ?? '',
    components: template.components,
    disableSystemAutoUnsubscribe: template.disableSystemAutoUnsubscribe ?? false,
  };
  return JSON.stringify(snapshot);
}
